import { createContext, useContext, useEffect, useState } from "react";
import {
    browserLocalPersistence,
    getIdTokenResult,
    onAuthStateChanged,
    setPersistence,
    signOut,
    User,
} from "firebase/auth";
import { auth, db } from "../services/firebase";
import { FirebaseCustomClaims } from "../types";
import { doc, getDoc } from "firebase/firestore";

// AppUser — solo i campi che usi nel FE
export type AppUser = {
    uid: string;
    displayName: string | null;
    email: string | null;
    phoneNumber: string | null;
    org?: string;
};

type AuthState = {
    user: AppUser | null;
    grants: string[];
    customClaims: FirebaseCustomClaims | null;
    token: string | null;
    loading: boolean;
};

type AuthContextType = AuthState & {
    logout: () => Promise<void>;
    refreshUser: () => Promise<void>;
    freshToken: () => Promise<string | undefined>;
};

const initialState: AuthState = {
    user: null,
    grants: [],
    customClaims: null,
    token: null,
    loading: true,
};

export const AuthContext = createContext<AuthContextType>({
    ...initialState,
    logout: async () => {},
    refreshUser: async () => {},
    freshToken: async () => undefined,
});

const mapUser = (u: User, data: Record<string, any>): AppUser => ({
    uid: u.uid,
    displayName: data.displayName ?? u.displayName,
    email: data.email ?? u.email,
    phoneNumber: data.phoneNumber ?? u.phoneNumber,
    org: data.org ?? null,
});

const fetchUserData = async (currentUser: User) => {
    const [tokenResult, token, userSnap] = await Promise.all([
        getIdTokenResult(currentUser),
        currentUser.getIdToken(true),
        getDoc(doc(db, "users", currentUser.uid)),
    ]);

    return {
        user: mapUser(currentUser, userSnap.data() ?? {}),
        customClaims: tokenResult.claims as unknown as FirebaseCustomClaims,
        token,
        grants: (userSnap.data()?.grants as string[]) ?? [],
    };
};

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [state, setState] = useState<AuthState>(initialState);

    useEffect(() => {
        let unsubscribe: () => void = () => {};
        setPersistence(auth, browserLocalPersistence).then(() => {
            unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
                if (currentUser) {
                    const data = await fetchUserData(currentUser);
                    setState({ ...data, loading: false });
                } else {
                    setState({ ...initialState, loading: false });
                }
            });
        });
        return () => unsubscribe();
    }, []);

    const logout = async () => {
        await signOut(auth);
        setState({ ...initialState, loading: false });
    };

    const freshToken = async (): Promise<string | undefined> =>
        auth.currentUser?.getIdToken(true);

    const refreshUser = async () => {
        if (!auth.currentUser) return;
        await auth.currentUser.reload();
        const data = await fetchUserData(auth.currentUser);
        setState({ ...data, loading: false });
    };

    return (
        <AuthContext.Provider
            value={{ ...state, logout, refreshUser, freshToken }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
